import React, { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../utils/notificationsApi';
import './NotificationBell.css';

// In-app bell — header widget with unread badge + dropdown.
// Live updates via Socket.io 'notification:new' (same backend that already
// powers the existing chat sockets — no new infra).
//
// Persona: we let the backend scope (admin sees admin-room notifs, user sees
// only their own). The component is identical for both — mount it in any
// header and the backend takes care of the rest.

const getSocketUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000';
    }
  }
  return window.location.origin;
};

const ICONS = {
  order_created: '⬢',
  order_status_changed: '◷',
  chat_message: '✦',
  admin_chat_reply: '✦',
  ticket_created: '◉',
  ticket_resolved: '✓',
  ticket_rejected: '✕',
  low_operator_gas: '⚠',
  default: '•',
};

const timeAgo = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(false); // brief micro-animation on new push
  const dropdownRef = useRef(null);
  const socketRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchNotifications({ status: 'all', limit: 30 });
      setItems(res?.notifications || []);
      setUnread(Number(res?.unread_count) || 0);
    } catch (_) {
      // silent — empty bell rather than an error toast
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + on every open (so the dropdown is fresh)
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  // Live Socket.io subscription
  useEffect(() => {
    const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('token') : null;
    if (!token) return undefined;

    const socket = io(getSocketUrl(), {
      auth: { token },
      transports: ['websocket', 'polling'],
      path: '/socket.io',
    });
    socketRef.current = socket;

    socket.on('notification:new', (n) => {
      setItems((prev) => [{ ...n, read: false }, ...prev].slice(0, 50));
      setUnread((u) => u + 1);
      setPulse(true);
      setTimeout(() => setPulse(false), 900);
      // Broadcast to any other component that wants to refresh on a new
      // notification (the analytics overview listens for this so KPI counts
      // jump immediately instead of waiting for the next 10s poll).
      try {
        window.dispatchEvent(new CustomEvent('phoneai:notification', { detail: n }));
      } catch (_) {}
    });

    socket.on('connect_error', () => { /* swallow — bell stays functional via polling load() */ });

    return () => {
      try { socket.off('notification:new'); socket.disconnect(); } catch (_) {}
    };
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const handleClickItem = useCallback(async (n) => {
    if (!n.read) {
      try {
        await markNotificationRead(n.id);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch (_) {}
    }
    if (n.link) {
      setOpen(false);
      // The order id lives in meta on order notifications (works even for OLD
      // notifications whose link predates the &order= deep-link).
      const metaOrder = n.meta?.order_db_id || n.meta?.orderDbId || null;
      try {
        const url = new URL(n.link, window.location.origin);
        const tab = url.searchParams.get('tab');
        const order = url.searchParams.get('order') || metaOrder;
        if (order && !url.searchParams.get('order')) url.searchParams.set('order', order);
        // Already inside admin → soft nav (no full reload / 403 flash) + open order.
        if (url.pathname.startsWith('/admin') && window.location.pathname.startsWith('/admin')) {
          window.history.pushState({}, '', url.pathname + url.search);
          if (tab) window.dispatchEvent(new CustomEvent('admin:setTab', { detail: { tab } }));
          if (order) window.dispatchEvent(new CustomEvent('admin:openOrder', { detail: { orderId: order } }));
          return;
        }
        // Full nav, but carry the order id so the target opens it on mount.
        window.location.assign(url.pathname + url.search);
        return;
      } catch (_) { /* fall through to a plain navigation */ }
      window.location.assign(n.link);
    }
  }, []);

  const handleMarkAll = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch (_) {}
  }, []);

  return (
    <div className="notif-bell" ref={dropdownRef}>
      <button
        type="button"
        className={`notif-bell-trigger ${pulse ? 'notif-bell-trigger--pulse' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        title="Notifications"
      >
        <span className="notif-bell-icon" aria-hidden="true">
          {/* Inline SVG bell so it matches stroke weight to the rest of header icons */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10 21a2 2 0 0 0 4 0" />
          </svg>
        </span>
        {unread > 0 && (
          <span className="notif-bell-badge" aria-label={`${unread} unread`}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-bell-panel" role="dialog" aria-label="Notifications">
          <div className="notif-bell-panel-head">
            <span className="notif-bell-panel-title">Notifications</span>
            <div className="notif-bell-panel-actions">
              {unread > 0 && (
                <button className="notif-bell-link" type="button" onClick={handleMarkAll}>
                  Mark all read
                </button>
              )}
              <button className="notif-bell-link" type="button" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          </div>

          <div className="notif-bell-list">
            {loading && items.length === 0 && (
              <div className="notif-bell-empty">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="notif-bell-empty">You're all caught up.</div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`notif-bell-item ${n.read ? '' : 'notif-bell-item--unread'}`}
                onClick={() => handleClickItem(n)}
              >
                <span className="notif-bell-item-icon" aria-hidden="true">
                  {ICONS[n.type] || ICONS.default}
                </span>
                <span className="notif-bell-item-body">
                  <span className="notif-bell-item-title">{n.title}</span>
                  {n.body && <span className="notif-bell-item-text">{n.body}</span>}
                  <span className="notif-bell-item-meta">{timeAgo(n.createdAt)}</span>
                </span>
                {!n.read && <span className="notif-bell-item-dot" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
